import API from "../../../services/rest/api";
import environment from "../../../constants/environments";
import { getDecryptedData, getEncryptedText } from "../../../utils/common";
import { containerAccount as fallbackContainerAccount } from "../../../utils/constants";
import { WebinarServiceError } from "../types/dto";

/** Container used by board host API when signing avatarUrl. */
export const HOST_AVATAR_CONTAINER = "aztatva-prod-storage-container";

export type HostImageUploadResult = {
  /** Persist this on the host record (avatarUrl) — filename only, same as report document_name. */
  storageKey: string;
  /** SAS URL from upload response — for immediate preview only (expires). */
  previewUrl: string;
};

function storageAccount(): string {
  return (
    environment.AZURE_STORAGE_ACCOUNT ||
    fallbackContainerAccount ||
    "mytatvaprodstorage"
  );
}

function fileToBase64Payload(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string") {
        reject(new Error("Could not read image"));
        return;
      }
      const base64 = result.includes("base64,")
        ? result.split("base64,")[1]
        : result;
      resolve(base64 || "");
    };
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.readAsDataURL(file);
  });
}

function extensionFor(file: File): "jpg" | "png" {
  return file.type === "image/png" ? "png" : "jpg";
}

/** Walk the upload API response and return the first usable remote URL. */
function findRemoteUrl(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return null;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findRemoteUrl(item);
      if (found) return found;
    }
    return null;
  }
  if (value && typeof value === "object") {
    for (const nested of Object.values(value as Record<string, unknown>)) {
      const found = findRemoteUrl(nested);
      if (found) return found;
    }
  }
  return null;
}

/**
 * SAS URL from push_to_azure_blob_storage — for preview only, not for avatarUrl on save.
 */
export function extractHostImagePreviewUrl(
  parsed: Record<string, unknown>,
): string {
  const fromResponse = findRemoteUrl(parsed);
  if (fromResponse) return fromResponse;

  throw new WebinarServiceError(
    "VALIDATION",
    "Image uploaded but the server did not return a URL. Check push_to_azure_blob_storage response.",
  );
}

/**
 * Upload host avatar via POST /azure_services/push_to_azure_blob_storage.
 * Returns a filename to save on the host and a signed URL for immediate preview.
 */
export async function uploadHostImage(file: File): Promise<HostImageUploadResult> {
  const account = storageAccount();
  const extensionType = extensionFor(file);
  const fileName = `board-host-${Date.now()}`;
  const storageKey = `${fileName}.${extensionType}`;
  const fileData = await fileToBase64Payload(file);

  if (!fileData) {
    throw new WebinarServiceError("VALIDATION", "Could not read image");
  }

  const payload = {
    containerAccount: account,
    containerName: HOST_AVATAR_CONTAINER,
    fileName,
    fileData,
    extensionType,
  };

  try {
    const encrypted = getEncryptedText(payload);
    const data = await API.UploadPdf(encrypted);
    const decryptedData = getDecryptedData(data);
    const parsed = JSON.parse(decryptedData) as Record<string, unknown>;

    if (String(parsed?.code) !== "1") {
      const message =
        typeof parsed?.message === "string" && parsed.message.trim()
          ? parsed.message.trim()
          : "Image upload failed";
      throw new WebinarServiceError("VALIDATION", message);
    }

    return {
      storageKey,
      previewUrl: extractHostImagePreviewUrl(parsed),
    };
  } catch (e) {
    if (e instanceof WebinarServiceError) throw e;
    const message =
      e instanceof Error && e.message ? e.message : "Image upload failed";
    throw new WebinarServiceError("VALIDATION", message);
  }
}
